import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material/styles';
import { DesktopDatePicker as MUIDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import FormControl from '~/common/components/Forms/FormControl';
import FormHelperText from '~/common/components/Forms/FormHelperText';
import FormLabel from '~/common/components/Forms/FormLabel';
import GenericTooltip from '~/common/components/GenericTooltip';
import palette from '~/theme/mui/palette';

function CalendarIcon() {
  return <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '0.75rem' }} />;
}

const CustomDatePicker = styled<any>(MUIDatePicker)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '2.5rem',
    backgroundColor: 'white',
    borderRadius: '4px',
    width: '100%',
    padding: '0 10px',
    '&:hover fieldset': {
      borderColor: theme.palette.gray[400],
    },

    '& .MuiInputAdornment-root:before': {
      backgroundColor: theme.palette.gray[100],
    },

    '&.Mui-focused:hover fieldset': {
      borderColor: theme.palette.blue[600],
    },
    '&.Mui-error': {
      borderColor: theme.palette.red[600],

      '&:hover fieldset': {
        borderColor: theme.palette.red[600],
      },
    },
    '& .MuiInputBase-input': {
      height: '100%',
      padding: '0',
      fontSize: '0.875rem',
    },
  },
  '& .MuiButtonBase-root': {
    color: 'black',
    height: '100%',

    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  },
  '& .MuiButtonBase-root.Mui-disabled': {
    color: theme.palette.gray[600],
  },
}));

type DatePickerProps = {
  name: string;
  label?: string | React.ReactNode;
  helperText?: string;
  placeholder?: string;
  ariaLabel?: string;
  value?: string;
  onChange?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  onFocus?: (...args: any[]) => any;
  minDate?: string | number | Date | dayjs.Dayjs;
  maxDate?: string | number | Date | dayjs.Dayjs;
  error?: React.ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  width?: string;
  sx?: any;
  fcStyle?: any;
  labelSx?: any;
  /** Choose where the date popover gets placed with respect to the text field. */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
  tabIndex?: number;
  maxWidth?: string | number;
  inputRef?: React.ForwardedRef<HTMLElement>;
  /** Used for testing purposes to identify the component. */
  'data-testid'?: string;
  'aria-describedby'?: string;
};

export default function DatePicker({
  name,
  label,
  helperText,
  placeholder,
  value,
  minDate,
  maxDate,
  error,
  width = undefined,
  placement = 'right-start',
  tabIndex,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  onChange,
  onBlur = () => {},
  onFocus,
  inputRef,
  sx = {},
  labelSx = {},
  fcStyle = {},
  ['data-testid']: dataTestId,
  ['aria-describedby']: ariaDescribedBy,
  maxWidth,
}: Readonly<DatePickerProps>) {
  const dayjsValue = useMemo(() => (value ? dayjs(value) : null), [value]);
  const minDateDayJsValue = useMemo(
    () => (minDate ? dayjs(minDate) : null),
    [minDate],
  );
  const maxDateDayJsValue = useMemo(
    () => (maxDate ? dayjs(maxDate) : null),
    [maxDate],
  );

  const formatDateToString = (date) =>
    date ? date.format('MM/DD/YYYY') : null;

  const handleDateChange = (date) => {
    if (date) {
      // If you need to format the date, do it here
      onChange?.(formatDateToString(date));
    } else {
      onChange?.(null);
    }
  };

  const handleDateBlur = (e) => {
    onBlur(e.target.value);
  };

  function handleClickOutsideOfFocus(e) {
    if (e.target.disabled) {
      return;
    }

    const notSelected = e.target.selectionStart === e.target.selectionEnd;
    const caretAtEnd = e.target.selectionStart === e.target.value?.length;

    // We are outside the date field range selection and the user just clicked into the field
    // Let's just select the end of the date for them.
    // This might be a MUI bug, so we should evaluate this if we update the @mui/x-date-pickers package.
    if (notSelected && caretAtEnd) {
      const lastSlash = e.target.value.lastIndexOf('/');
      const lastChar = e.target.value.length;
      e.target.setSelectionRange(lastSlash + 1, lastChar);
    }
  }

  let doubleClicked = false;
  function onDoubleClick(e) {
    doubleClicked = true;
    e.target.select();
  }

  function onKeyDown(e) {
    const selection = e.target.value.slice(
      e.target.selectionStart,
      e.target.selectionEnd,
    );

    // Allow resetting value on double click
    if (
      doubleClicked &&
      selection === e.target.value &&
      dayjsValue?.isValid?.() &&
      !Number.isNaN(Number.parseInt(e.key))
    ) {
      onChange(e.key);
      doubleClicked = false;
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (selection === '') {
        // No selection. Treat it like they just clicked to the end and select the year field.
        e.preventDefault();
        handleClickOutsideOfFocus(e);
      } else if (/^[DY]{2,4}$/.test(selection)) {
        // They have a blank selected field select.
        // Instead of trying to delete again, move to the previous field and delete it (e.g. let the event propagate).
        e.target.dispatchEvent(
          new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }),
        );
      }
    }
  }

  return (
    <FormControl
      required={isRequired}
      disabled={isDisabled}
      error={!!error}
      fullWidth
      sx={fcStyle}
    >
      {label &&
        (typeof label === 'string' ? (
          <FormLabel
            htmlFor={name}
            sx={{
              '&&': { marginBottom: 0 },
              '&[data-disabled]': { opacity: 1 },
              ...labelSx,
            }}
            id={`${name}-label`}
          >
            {label}
            {helperText && <GenericTooltip label={helperText} />}
          </FormLabel>
        ) : (
          <FormLabel htmlFor={name} id={`${name}-label`} sx={labelSx}>
            {label}
          </FormLabel>
        ))}
      <CustomDatePicker
        className="date-picker"
        name={name}
        id={name}
        value={dayjsValue}
        onChange={handleDateChange}
        minDate={minDateDayJsValue}
        maxDate={maxDateDayJsValue}
        onBlur={handleDateBlur}
        slots={{
          openPickerIcon: CalendarIcon,
        }}
        disabled={isDisabled}
        readOnly={isReadOnly}
        slotProps={{
          field: {
            sx: {
              width: width || '100%',
              ...sx,
              maxWidth,
            },
          },
          textField: {
            id: name,
            fullWidth: true,
            placeholder,
            onBlur: handleDateBlur,
            onFocus,
            onClick: handleClickOutsideOfFocus,
            onDoubleClick,
            onKeyDown,
            error: !!error,
            inputRef,
            inputProps: {
              'aria-labelledby': `${name}-label`,
              tabIndex: tabIndex,
              'data-testid': dataTestId,
              'aria-describedby': ariaDescribedBy,
            },
          },
          popper: {
            placement,
            onBlur,
            sx: {
              zIndex: 1500,
            },
          },
          openPickerButton: {
            onFocus,
          },
        }}
      />
      {error && typeof error !== 'boolean' && (
        <FormHelperText color={palette?.error?.main} id={`${name}-error`}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
